import * as React from 'react';
import {
    createRoutesFromChildren,
    matchRoutes,
    Routes as RouterRoutes,
    useLocation,
    useNavigationType,
} from 'react-router-dom';
import {
    isExpectedTransitionError,
    isKnownHarmlessBrowserDomError,
    isStaleChunkError,
} from '../utils/errorClassifiers';

type SentryModule = typeof import('@sentry/react');
type SentryOptions = Parameters<SentryModule['init']>[0] & {
    enableLogs?: boolean;
    tracePropagationTargets?: Array<string | RegExp>;
};
type SentryBreadcrumb = import('@sentry/react').Breadcrumb;
type SentryScope = {
    setTag: (key: string, value: string) => void;
    setFingerprint: (fingerprint: string[]) => void;
    setExtra: (key: string, value: unknown) => void;
    setContext: (key: string, value: Record<string, unknown>) => void;
};
type SentryTagValue = string | number | boolean | null | undefined;
type CaptureContext = Record<string, unknown> & {
    tags?: Record<string, SentryTagValue>;
    extra?: Record<string, unknown>;
    fingerprint?: string[];
    context?: Record<string, unknown>;
    contexts?: Record<string, Record<string, unknown>>;
};

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || '';
const ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'development';
const RELEASE = import.meta.env.VITE_SENTRY_RELEASE || '';
const IS_PRODUCTION = ENVIRONMENT === 'production';
const SEND_USER_EMAIL = import.meta.env.VITE_SENTRY_SEND_USER_EMAIL === 'true';

let _sentryInitialized = false;
let _sentryModule: SentryModule | null = null;
let _sentryModulePromise: Promise<SentryModule> | null = null;
let _sentryInitPromise: Promise<void> | null = null;

declare global {
    interface Window {
        __TC_SENTRY_INIT_PROMISE__?: Promise<void>;
    }
}

const loadSentry = async () => {
    if (!_sentryModulePromise) {
        _sentryModulePromise = import('@sentry/react').then(module => {
            _sentryModule = module;
            return module;
        });
    }

    return _sentryModulePromise;
};

const parseRate = (value: unknown, fallback: number): number => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.max(0, Math.min(1, parsed));
};

const tracesSampleRate = parseRate(
    import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
    IS_PRODUCTION ? 0.2 : 1.0,
);
const sensitiveKeyPattern = /(authorization|cookie|token|secret|password|api[_-]?key|stripe|paypal|resend|proxy|jwt|sentry_auth)/i;

const sanitizeValue = (value: unknown, depth = 0): unknown => {
    if (depth > 5) return '[Truncated]';
    if (Array.isArray(value)) {
        return value.slice(0, 50).map((item) => sanitizeValue(item, depth + 1));
    }
    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>).map(([key, child]) => [
                key,
                sensitiveKeyPattern.test(key) ? '[Filtered]' : sanitizeValue(child, depth + 1),
            ]),
        );
    }
    if (typeof value === 'string' && value.length > 1000) {
        return `${value.slice(0, 1000)}...[Truncated]`;
    }
    return value;
};

const toSentryContext = (value: unknown): Record<string, unknown> => {
    const sanitized = sanitizeValue(value);
    if (sanitized && typeof sanitized === 'object' && !Array.isArray(sanitized)) {
        return sanitized as Record<string, unknown>;
    }
    return { value: sanitized };
};

const applyCaptureContext = (scope: SentryScope, context?: CaptureContext) => {
    if (!context) return;

    const {
        tags,
        extra,
        fingerprint,
        context: defaultContext,
        contexts,
        ...namedContexts
    } = context;

    if (tags) {
        Object.entries(tags).forEach(([key, value]) => {
            if (value === null || value === undefined) return;
            scope.setTag(key, String(value));
        });
    }

    if (fingerprint?.length) {
        scope.setFingerprint(fingerprint);
    }

    if (extra) {
        Object.entries(extra).forEach(([key, value]) => {
            if (value === undefined) return;
            scope.setExtra(key, sanitizeValue(value));
        });
    }

    if (defaultContext) {
        scope.setContext('context', toSentryContext(defaultContext));
    }

    if (contexts) {
        Object.entries(contexts).forEach(([key, value]) => {
            scope.setContext(key, toSentryContext(value));
        });
    }

    Object.entries(namedContexts).forEach(([key, value]) => {
        if (value === undefined) return;
        scope.setContext(key, toSentryContext(value));
    });
};

const getTracePropagationTargets = (): Array<string | RegExp> => [
    /^https:\/\/(www\.)?traffic-creator\.com/,
    /^https:\/\/(www\.)?traffic-bot\.com/,
    /^https:\/\/localhost(:\d+)?/,
    /^http:\/\/localhost(:\d+)?/,
    /^http:\/\/127\.0\.0\.1(:\d+)?/,
    /^\/(auth|users|projects|transactions|tickets|settings|subscriptions|stripe|paypal|admin|api|tools|health|stats|start|stop|proxy)/,
];

// Keep public routes free of the Sentry SDK chunk. Error reporting still works
// after initSentry() runs on authenticated surfaces.
export const SentryRoutes = RouterRoutes;

export const reactErrorHandler = () => (error: unknown, errorInfo: unknown) => {
    captureException(error, {
        context: { source: 'ReactRoot' },
        extra: { errorInfo },
    });
};

export function initSentry() {
    if (_sentryInitialized || !SENTRY_DSN) {
        return _sentryInitPromise || Promise.resolve();
    }

    if (_sentryInitPromise) return _sentryInitPromise;
    if (window.__TC_SENTRY_INIT_PROMISE__) {
        _sentryInitPromise = window.__TC_SENTRY_INIT_PROMISE__;
        return _sentryInitPromise;
    }

    _sentryInitPromise = loadSentry().then(Sentry => {
        if (_sentryInitialized) return;

        // A stale entry bundle and a freshly deployed entry bundle can briefly
        // coexist during an in-app release hand-off. Both share the Sentry SDK
        // singleton, so do not install a second Session Replay integration.
        if (Sentry.getClient?.()) {
            _sentryInitialized = true;
            return;
        }

        const options: SentryOptions = {
            dsn: SENTRY_DSN,
            environment: ENVIRONMENT,
            release: RELEASE || undefined,
            sendDefaultPii: false,
            tracesSampleRate,
            tracePropagationTargets: getTracePropagationTargets(),
            tunnel: import.meta.env.VITE_SENTRY_TUNNEL || '/api/sentry-tunnel',
            integrations: [
                Sentry.reactRouterV7BrowserTracingIntegration({
                    useEffect: React.useEffect,
                    useLocation,
                    useNavigationType,
                    createRoutesFromChildren,
                    matchRoutes,
                }),
                Sentry.feedbackIntegration({
                    autoInject: false,
                    showBranding: false,
                    enableScreenshot: false,
                    showEmail: false,
                    showName: false,
                    triggerLabel: '',
                    triggerAriaLabel: 'Send feedback',
                    formTitle: 'Send feedback',
                    messageLabel: 'Message',
                    messagePlaceholder: 'What happened?',
                    submitButtonLabel: 'Send',
                    tags: { surface: 'authenticated_app' },
                }),
            ],
            beforeSend(event, hint) {
                const error = hint?.originalException;
                const isAlertEvent = event.tags?.alert_scope === 'dashboard_route_loading';
                if (
                    !isAlertEvent &&
                    (
                        isExpectedTransitionError(error) ||
                        isKnownHarmlessBrowserDomError(error) ||
                        isStaleChunkError(error)
                    )
                ) {
                    return null;
                }

                if (error instanceof Error && error.message?.includes('ResizeObserver')) {
                    return null;
                }

                if (error instanceof Error && error.message?.includes('Failed to fetch')) {
                    const frames = event.exception?.values?.[0]?.stacktrace?.frames;
                    if (frames?.some((frame) => frame.filename?.includes('chrome-extension'))) {
                        return null;
                    }
                }

                if (error instanceof Error) {
                    const message = error.message || '';
                    if (message.includes('insertBefore') || message.includes('@context') || message.includes('toLowerCase')) {
                        const frames = event.exception?.values?.[0]?.stacktrace?.frames;
                        if (frames?.some((frame) => frame.filename?.includes('partytown'))) {
                            return null;
                        }
                    }
                }

                try {
                    const lastRequestId = sessionStorage.getItem('last_request_id');
                    if (lastRequestId) {
                        event.tags = { ...event.tags, request_id: lastRequestId };
                    }
                } catch {
                    // sessionStorage may be blocked.
                }

                return sanitizeValue(event) as typeof event;
            },
            ignoreErrors: [
                /MetaMask/i,
                /chromePort disconnected/i,
                /\[ChromeTransport\]/i,
                /Failed to fetch/i,
                /Load failed/i,
                /NetworkError when attempting to fetch resource/i,
                'top.GLOBALS',
                'canvas.contentDocument',
                'MyApp_RemoveAllHighlights',
                'atomicFindClose',
                'fb_xd_fragment',
                'chrome-extension://',
                'gtag is not defined',
                'gtm.js',
                'tawk',
                "Failed to execute 'removeChild' on 'Node'",
                'The node to be removed is not a child of this node.',
                "Failed to execute 'insertBefore' on 'Node'",
                'The child can not be found in the parent node',
                /partytown/i,
                /Failed to fetch dynamically imported module/i,
                /Loading chunk .* failed/i,
                'Script error.',
                'Non-Error promise rejection captured',
            ],
            allowUrls: [
                /https?:\/\/(www\.)?traffic-creator\.com/,
                /https?:\/\/(www\.)?traffic-bot\.com/,
                /https?:\/\/localhost/,
                /http:\/\/127\.0\.0\.1/,
            ],
            denyUrls: [
                /extensions\//i,
                /^chrome:\/\//i,
                /^chrome-extension:\/\//i,
                /^moz-extension:\/\//i,
                /embed\.tawk\.to/i,
                /googletagmanager\.com/i,
                /google-analytics\.com/i,
                /doubleclick\.net/i,
            ],
        };

        if (import.meta.env.VITE_SENTRY_ENABLE_LOGS !== 'false') {
            options.enableLogs = true;
        }

        Sentry.init(options);
        _sentryInitialized = true;
    });

    window.__TC_SENTRY_INIT_PROMISE__ = _sentryInitPromise;

    return _sentryInitPromise;
}

export function setSentryUser(user: { id: string; email?: string; role?: string; plan?: string }) {
    if (!_sentryInitialized || !_sentryModule) return;
    _sentryModule.setUser({
        id: user.id,
        ...(SEND_USER_EMAIL && user.email ? { email: user.email } : {}),
    });
    if (user.role) _sentryModule.setTag('user_role', user.role);
    if (user.plan) _sentryModule.setTag('user_plan', user.plan);
}

export function clearSentryUser() {
    if (!_sentryInitialized || !_sentryModule) return;
    _sentryModule.setUser(null);
}

export function captureException(error: unknown, context?: Record<string, unknown>) {
    if (!_sentryInitialized || !_sentryModule) return;
    if (context) {
        _sentryModule.withScope((scope) => {
            applyCaptureContext(scope, context as CaptureContext);
            _sentryModule?.captureException(error);
        });
    } else {
        _sentryModule.captureException(error);
    }
}

export function captureMessage(
    message: string,
    level: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug' = 'info',
    context?: Record<string, unknown>,
) {
    if (!_sentryInitialized || !_sentryModule) return;
    if (context) {
        _sentryModule.withScope((scope) => {
            applyCaptureContext(scope, context as CaptureContext);
            _sentryModule?.captureMessage(message, level);
        });
        return;
    }
    _sentryModule.captureMessage(message, level);
}

export function addBreadcrumb(breadcrumb: SentryBreadcrumb) {
    if (!_sentryInitialized || !_sentryModule) return;
    _sentryModule.addBreadcrumb(sanitizeValue(breadcrumb) as SentryBreadcrumb);
}

export async function showSentryFeedback() {
    if (!_sentryInitialized) {
        await initSentry();
    }
    if (!_sentryInitialized || !_sentryModule) return;

    const feedback = _sentryModule.getFeedback?.();
    if (!feedback) return;
    const dialog = await feedback.createForm({
        tags: { surface: window.location.pathname.startsWith('/admin') ? 'admin' : 'dashboard' },
    });
    try {
        dialog.appendToDom();
        dialog.open();
    } catch (error) {
        if (!isKnownHarmlessBrowserDomError(error)) {
            captureException(error, { context: { source: 'SentryFeedback' } });
        }
    }
}

export function getSentryErrorBoundary() {
    return _sentryInitialized ? _sentryModule?.ErrorBoundary || null : null;
}
